import ResourceTyped from 'Resources/ResourceTyped';
import extractApiError from 'Resources/extractApiError';

// Usage / credits / pricing / policies / access governance resource, split
// from the Assistant god resource (WEB-7958). Same URLs — pure client-side
// reorg. Pairs with the Phase 1 controller split (E.5).

export type AdminUsage = {
	period: { label: string; from: string; to: string };
	orgBudget: {
		spentCents: number;
		spentCreditsSek: number;
		limitSek: number;
		dailySeries: number[];
		dailySeriesCreditsSek: number[];
	};
	users: Array<{
		userId: number;
		name: string;
		role: string | null;
		personalSpendCents: number;
		personalSpendCreditsSek: number;
		orgSpendCents: number;
		orgSpendCreditsSek: number;
		agentCount: number;
		canCreateAgents: boolean;
		canAccessWorkspace: boolean;
		isAdmin: boolean;
		/** WEB-6650 — explicit per-user override; null = inherit default. */
		aiCreditCapOverrideSek: number | null;
		/** WEB-6650 — resolved cap (override → org default). 0 = unlimited. */
		effectivePersonalCreditCapSek: number;
	}>;
	agents: Array<{
		agentId: number;
		name: string;
		visibility: 'org' | 'personal';
		ownerUserId: number | null;
		ownerName: string | null;
		avatarUrl: string | null;
		tasks: number;
		successPct: number;
		spendCents: number;
		spendCreditsSek: number;
	}>;
	promotionPending: number;
	kpis: {
		totalSpendCents: number;
		totalSpendCreditsSek: number;
		tasks: number;
		avgSuccessPct: number;
		topSpenderUserId: number | null;
		mostUsedAgentId: number | null;
	};
};

export type CreditsSnapshot = {
	period: { label: string; from: string; to: string };
	allocatedSek: number;
	/** WEB-10769 — the two parts `allocatedSek` is made of: the licence
	 *  allocation and any recurring extra credits bought on the subscription.
	 *  Always sums to `allocatedSek`; render the split, do maths on the total. */
	licenseAllocatedSek: number;
	recurringExtraSek: number;
	/** WEB-10770 — prepaid one-off credits carried over from earlier months.
	 *  NOT part of `allocatedSek`: it doesn't reset, and it is only spent once
	 *  the month's allocation is used up. Show it on its own line. */
	oneOffBalanceSek: number;
	consumedSek: number;
	remainingSek: number;
	overageSek: number;
	activeUserCount: number;
	effectiveUsdToSekRate: number;
	marginMultiplier: number;
	/** Current-month live meter. ONLY populated when the snapshot's
	 *  period covers "now" (current month, last30d, all) — null for past
	 *  periods, so don't wire banners to a past-period snapshot and expect
	 *  live state. Also null when the meter is unreadable (fail open). */
	liveConsumedSek: number | null;
	/** Live meter ≥ the org-wide monthly cap right now. Always false
	 *  for past periods or an unreadable meter — see liveConsumedSek. */
	creditsExhausted: boolean;
	perUser: Array<{
		userId: number;
		name: string | null;
		costUsd: number;
		creditsSek: number;
		callCount: number;
		/** WEB-6650 — resolved per-user credit cap (SEK). 0 = unlimited. */
		effectivePersonalCreditCapSek: number;
	}>;
	perAgent: Array<{
		agentId: number;
		name: string | null;
		visibility: 'org' | 'personal' | null;
		costUsd: number;
		creditsSek: number;
		callCount: number;
	}>;
	perTool: Array<{ feature: string; costUsd: number; creditsSek: number; callCount: number }>;
	perDay: Array<{ day: string; costUsd: number; creditsSek: number; callCount: number }>;
	customerPaidConnections: Array<{
		feature: string;
		type: string | null;
		callCount: number;
		totalDurationMs: number | null;
	}>;
	customerPaidPerUser: Array<{ userId: number; name: string | null; callCount: number }>;
	customerPaidPerAgent: Array<{
		agentId: number;
		name: string | null;
		visibility: 'org' | 'personal' | null;
		callCount: number;
	}>;
	customerPaidPerDay: Array<{ day: string; callCount: number }>;
	// WEB-6651: notetaker (and any future tool) usage covered by a legacy
	// addon. Same shape as customerPaidConnections; differentiated server-side
	// by `metadata.coverage === 'addon'` on the ai_tracking row. Never folded
	// into credits — the addon already pays for it.
	addonCoveredUsage: Array<{
		feature: string;
		type: string | null;
		callCount: number;
		totalDurationMs: number | null;
	}>;
};

export type PriceBookItem = {
	toolKey: string;
	vendor: string;
	category: 'llm' | 'web' | 'notetaker' | 'other';
	unit: string;
	pricePerUnitSek: number;
};

// WEB-7168 — Forecast admin types. Hand-maintained mirrors of the response
// shapes in `api/src/controller/customer/ai/forecastAdmin.ts` (the UI can't
// import API types) — update both when the shape changes.
export type ForecastSettings = {
	continuousRefresh: boolean;
};

export type ForecastCostEstimate = {
	deal: {
		candidateDeals: number;
		estimatedRelevantDeals: number;
		willTranslate: boolean;
		monthlyCredits: number | null;
	};
	pipeline: {
		wouldCallLLM: boolean;
		reasonSkipped: string | null;
		monthlyCredits: number | null;
	};
	totalMonthlyCredits: number | null;
	estimatedAt: string;
};

export type PromotionRequest = {
	id: number;
	agentId: number;
	requestedByUserId: number;
	pitchText: string | null;
	status: 'pending' | 'approved' | 'declined';
	decidedByUserId?: number | null;
	decidedAt?: string | null;
	regDate: string;
	modDate: string;
};

export type UserUsage = {
	personalSpendCents: number;
	personalSpendCreditsSek: number;
	personalCapSek: number;
	/** WEB-6650 — resolved cap (override → org default). 0 = unlimited. */
	effectivePersonalCreditCapSek: number;
	agents: Array<{
		agentId: number;
		name: string;
		visibility: 'org' | 'personal';
		status: string;
		avatarUrl: string | null;
		budgetMonthlySek: number;
		spentMonthlyCents: number;
		spentMonthlyCreditsSek: number;
		tasksCompleted14d: number;
		tasksFailed14d: number;
		pausedReason: string | null;
	}>;
	totalSpendCents: number;
	totalSpendCreditsSek: number;
	tasksCompleted14d: number;
	tasksFailed14d: number;
	successRate14d: number;
	histogram: Array<{
		day: string;
		completed: number;
		failed: number;
		spendCents: number;
		spendCreditsSek: number;
	}>;
	pool: {
		period: { label: string; from: string; to: string };
		allocatedSek: number;
		consumedSek: number;
		remainingSek: number;
		overageSek: number;
		userCreditsSek: number;
		userSharePct: number;
		/** Current-month live meter (null when unreadable — fail open). */
		liveConsumedSek: number | null;
		/** Live meter ≥ the org-wide monthly cap right now. */
		creditsExhausted: boolean;
	} | null;
};

export type AiAgentPolicies = {
	/**
	 * Org-wide default per-user monthly cap on personal AI agent spend (SEK).
	 * Also the WEB-6650 fallback when a user has no `ai_credit_cap_sek`
	 * override. 0 = unlimited.
	 */
	personalCapSek: number;
	orgMonthlyBudgetSek: number;
	promotionMinTasks: number;
	promotionMinSuccessPct: number;
	minExecutionIntervalMinutes: number;
	permissionUpgradeMinApprovals: number;
};

/**
 * Admin policies response. Mirrors {@link AiAgentPolicies} plus the org-budget
 * ceiling: a hard cap the admin cannot raise the org budget above (flag-only /
 * unpaid trial orgs), or `null` when the account has no such ceiling. When set,
 * the limits UI locks the org-budget field and explains why a higher value
 * won't stick. Kept off {@link AiAgentPolicies} so the editable-field keys stay
 * purely numeric.
 */
export type AiAgentPoliciesResponse = AiAgentPolicies & { orgBudgetCeilingSek: number | null };

class AssistantUsage extends ResourceTyped<UserUsage> {
	constructor() {
		super('assistant/usage');
	}

	getUserUsage(): Promise<UserUsage> {
		return fetch('/api/v2/assistant/usage')
			.then(res => res.json())
			.then(res => res.data);
	}

	getAdminUsage(period?: string): Promise<AdminUsage> {
		const qs = period ? `?period=${encodeURIComponent(period)}` : '';
		return fetch(`/api/v2/assistant/admin/usage${qs}`)
			.then(res => res.json())
			.then(res => res.data);
	}

	getAccountCredits(
		period?: string,
		filters?: { userIds?: number[]; agentIds?: number[]; features?: string[] }
	): Promise<CreditsSnapshot | null> {
		const params = new URLSearchParams();
		if (period) params.set('period', period);
		if (filters?.userIds?.length) params.set('users', filters.userIds.join(','));
		if (filters?.agentIds?.length) params.set('agents', filters.agentIds.join(','));
		if (filters?.features?.length) params.set('features', filters.features.join(','));
		const qs = params.toString();
		return fetch(`/api/v2/assistant/admin/credits${qs ? `?${qs}` : ''}`)
			.then(res => res.json())
			.then(res => res.data ?? null);
	}

	getToolPricing(): Promise<PriceBookItem[]> {
		return fetch('/api/v2/assistant/admin/tool-pricing')
			.then(res => res.json())
			.then(res => res.data ?? []);
	}

	// WEB-7168 — forecast admin. Throws on non-2xx (403/429/4xx are
	// expected responses here) so the card can surface the error.
	async getForecastSettings(): Promise<ForecastSettings> {
		const res = await fetch('/api/v2/assistant/admin/forecast-settings');
		const body = await res.json().catch(() => ({}));
		if (!res.ok) throw extractApiError(body, res.status, 'Forecast settings request failed');
		return body.data;
	}

	async updateForecastSettings(patch: Partial<ForecastSettings>): Promise<ForecastSettings> {
		const res = await fetch('/api/v2/assistant/admin/forecast-settings', {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(patch)
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) throw extractApiError(body, res.status, 'Forecast settings update failed');
		return body.data;
	}

	async getForecastCostEstimate(): Promise<ForecastCostEstimate> {
		const res = await fetch('/api/v2/assistant/admin/forecast-cost-estimate');
		const body = await res.json().catch(() => ({}));
		if (!res.ok) throw extractApiError(body, res.status, 'Forecast cost estimate failed');
		return body.data;
	}

	requestPromotion(agentId: number, pitchText: string): Promise<PromotionRequest> {
		return fetch('/api/v2/assistant/promotion-requests', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ agentId, pitchText })
		})
			.then(res => res.json())
			.then(res => res.data);
	}

	getAiAgentPolicies(): Promise<AiAgentPoliciesResponse> {
		return fetch('/api/v2/assistant/admin/policies')
			.then(res => res.json())
			.then(res => res.data);
	}

	saveAiAgentPolicies(updates: Partial<AiAgentPolicies>): Promise<AiAgentPoliciesResponse> {
		return fetch('/api/v2/assistant/admin/policies', {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updates)
		})
			.then(res => res.json())
			.then(res => res.data);
	}

	getAgentCreatorUsers(): Promise<{ userIds: number[] }> {
		return fetch('/api/v2/assistant/admin/agent-creator-users')
			.then(res => res.json())
			.then(res => res.data);
	}

	saveAgentCreatorUsers(userIds: number[]): Promise<{ userIds: number[] }> {
		return fetch('/api/v2/assistant/admin/agent-creator-users', {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ userIds })
		}).then(async res => {
			if (!res.ok) throw new Error(`saveAgentCreatorUsers ${res.status}`);
			const body = await res.json();
			if (body?.error) throw new Error(String(body.error?.message ?? body.error));
			return body.data;
		});
	}

	getWorkspaceUsers(): Promise<{ userIds: number[] }> {
		return fetch('/api/v2/assistant/admin/workspace-users')
			.then(res => res.json())
			.then(res => res.data);
	}

	saveWorkspaceUsers(userIds: number[]): Promise<{ userIds: number[] }> {
		return fetch('/api/v2/assistant/admin/workspace-users', {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ userIds })
		}).then(async res => {
			if (!res.ok) throw new Error(`saveWorkspaceUsers ${res.status}`);
			const body = await res.json();
			if (body?.error) throw new Error(String(body.error?.message ?? body.error));
			return body.data;
		});
	}

	/**
	 * WEB-6650 — set or clear a single user's AI credit cap override.
	 * Pass `null` for `sek` to remove the override (user falls back to the
	 * org-wide `aiAgent.personalCapSek`).
	 */
	saveUserCreditCap(userId: number, sek: number | null): Promise<{ userId: number; sek: number | null }> {
		return fetch(`/api/v2/assistant/admin/users/${userId}/credit-cap`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ sek })
		})
			.then(res => res.json())
			.then(res => res.data);
	}

	getMyPermissions(): Promise<{ canCreateAgents: boolean; canAccessWorkspace: boolean } | undefined> {
		return this._getRequest('', {
			methodName: 'getMyPermissions',
			_url: 'assistant/me/permissions',
			expectedStatuses: [403]
		})
			.then(res => res.data?.data)
			.catch(err => {
				// 403 is the MAJORITY response here (~65% of production traffic): most customers
				// have no AI licence, and AppSidebar calls this on every page load. It is the
				// answer to "do I have access", not an error, so resolve — rejecting toasts at
				// EmbedAIChat and stops loadPermissions from caching the negative answer.
				if (err?.response?.status === 403) return undefined;
				throw err;
			});
	}
}

const resource = new AssistantUsage();

export default resource;
